import { NativeModules } from 'react-native';
import Toast from 'react-native-toast-message';
import type { ReactNativeBridge } from '../../../native-module';
import { isWeb } from '../isWeb';
import type { Simplify } from 'type-fest';
import { logAdEvent as logAdEventFn } from './logAdEvent/logAdEvent';
import { logAnalyticsEvent as logAnalyticsEventFn } from './logAnalyticsEvent/logAnalyticsEvent';
import { getWebBaseURL } from '../getWebBaseURL';
import { convertDeeplinkToWebLink } from '../convertDeeplinkToWebLink/convertDeeplinkToWebLink';
import type { LogAnalyticsEventParams } from './logAnalyticsEvent/types';
import type { LogAdEventParams } from './logAdEvent/types';
import { mergeLogging } from './logAnalyticsEvent/mergeLogging';
import DeviceInfo from '../DeviceInfo';
import { compareVersions } from 'compare-versions';
import { isAndroid } from '../isAndroid';

type PathString = `/${string}`;

class Bridge {
  toast(message: string) {
    if (isWeb) {
      Toast.show({
        type: 'success',
        text1: message,
        position: 'bottom',
      });
      return;
    }
    NativeModules.ReactNativeBridge.toast(message);
  }

  vibrate(style: string) {
    if (isWeb) return;
    if (isAndroid) return;

    NativeModules.ReactNativeBridge.vibrate(style);
  }

  assertLogin(
    params: Simplify<Parameters<ReactNativeBridge['assertLogin']>[0]>,
  ) {
    if (isWeb) {
      getWebBaseURL().then(baseURL => {
        location.href = `${baseURL}/login`;
      });
      return;
    }
    NativeModules.ReactNativeBridge.assertLogin(params);
  }
  /**
   * - 찜 싱크
   * - 웹의 경우 싱크하지 않습니다.
   */
  likeGoods(
    goodsParams: Simplify<Parameters<ReactNativeBridge['likeGoods']>[0]>,
  ) {
    if (isWeb) {
      return;
    }
    NativeModules.ReactNativeBridge.likeGoods(goodsParams);
  }

  /**
   * 마켓 좋아요 싱크
   * 웹의 경우 싱크하지 않아도 됩니다.
   * @param marketParams
   */
  favoriteMarket(
    marketParams: Simplify<Parameters<ReactNativeBridge['favoriteMarket']>[0]>,
  ) {
    if (isWeb) {
      return;
    }
    try {
      NativeModules.ReactNativeBridge.favoriteMarket(marketParams);
    } catch {
      /* empty */
    }
  }

  /**
   * 아웃핏 북마크 싱크
   * 웹의 경우 싱크하지 않습니다.
   */
  bookmarkOutfit(
    params: Simplify<Parameters<ReactNativeBridge['bookmarkOutfit']>[0]>,
  ) {
    if (isWeb) {
      return;
    }
    NativeModules.ReactNativeBridge.bookmarkOutfit(params);
  }

  /**
   * 아웃핏 북마크에 대한 로컬 저장소 값을 가져옵니다.
   */
  getLocalOutfitBookmark() {
    if (isWeb) {
      return null;
    }
    try {
      return NativeModules.ReactNativeBridge.getLocalOutfitBookmark({});
    } catch {
      return null;
    }
  }

  async openDeeplink(
    link: string,
    {
      webCallback,
    }: {
      /**
       * - isWeb일때 기본동작이 아닌 webCallback이 실행됩니다.
       */
      webCallback: (...args: unknown[]) => unknown;
    } = {
      webCallback: async () => {
        const webLink = await convertDeeplinkToWebLink(link);
        if (webLink == null) {
          location.href = '/';
          return;
        }
        location.href = webLink;
      },
    },
  ) {
    if (isWeb) {
      webCallback();
      return;
    }
    NativeModules.ReactNativeBridge.openDeeplink(link);
  }

  async openPath(
    path: PathString,
    {
      webPath,
    }: {
      webPath: PathString;
    } = {
      webPath: path,
    },
  ) {
    await this.openDeeplink(`ably:/${path}`, {
      webCallback: async () => {
        const baseURL = await getWebBaseURL();
        location.href = `${baseURL}${webPath}`;
      },
    });
  }

  openGoodsShare(params: {
    goods_sno: number;
    affiliate_reward: {
      expected_reward: number;
      share_nudging?: {
        title: string;
        deeplink: string;
      } | null;
      logging?: Record<string, string | number | boolean | null> | null;
    };
    referral_reward?: {
      amount: number;
      deeplink: string;
    };
    is_by_screenshot?: boolean;
    logging_analytics?: Record<string, string | number | boolean | null>;
  }) {
    if (isWeb) {
      const url = `${location.origin}/goods/${params.goods_sno}`;
      // if (navigator.share) {
      //   navigator.share({ url }).catch(() => {});
      // } else {
      navigator.clipboard?.writeText(url).catch(() => {});
      // }
      return;
    }
    NativeModules.ReactNativeBridge.openGoodsShare(params);
  }

  /**
   * - react-native 인스턴스 종료하기
   * @param params
   */
  closeInstance(params: { instanceId?: string; animated?: boolean }) {
    if (isWeb || !params.instanceId) {
      return;
    }

    NativeModules.ReactNativeBridge.closeInstance({
      instanceId: params.instanceId,
      animated: params.animated,
    });
  }

  logAnalyticsEvent({
    logging,
    analytics,
    ably_only_log,
    ...rest
  }: LogAnalyticsEventParams) {
    const merged = mergeLogging({ logging, analytics, ably_only_log });
    const params = { ...rest, ...merged };
    if (__DEV__) {
      console.log('📕 logAnalyticsEvent', params);
    }
    logAnalyticsEventFn(params);
  }

  logAdEvent(params: LogAdEventParams) {
    if (__DEV__) {
      console.log('💸 logAdEvent', params);
    }
    logAdEventFn(params);
  }
  /**
   * 상품 좋아요에 대한 로컬 저장소 값을 가져옵니다
   */
  getLocalGoodsLike() {
    if (isWeb) {
      return null;
    }
    try {
      return NativeModules.ReactNativeBridge.getLocalGoodsLike({});
    } catch {
      return null;
    }
  }

  /**
   * 마켓 좋아요에 대한 로컬 저장소 값을 가져옵니다.
   */
  getLocalMarketFavorite() {
    if (isWeb) {
      return null;
    }
    try {
      return NativeModules.ReactNativeBridge.getLocalMarketFavorite({});
    } catch {
      return null;
    }
  }

  setScreenName(params: { instanceId?: string; name: string }) {
    if (isWeb) {
      return;
    }
    try {
      NativeModules.ReactNativeBridge.setScreenName({
        instanceId: params.instanceId,
        name: params.name,
      });
    } catch {
      /* empty*/
    }
  }

  reportTTFD(params: Parameters<ReactNativeBridge['reportTTFD']>[0]): void {
    if (isWeb) {
      return;
    }
    try {
      NativeModules.ReactNativeBridge.reportTTFD(params);
    } catch {
      /* empty */
    }
  }

  async updateMemberMe(
    params: Parameters<ReactNativeBridge['updateMemberMe']>[0],
  ): Promise<void> {
    if (isWeb) {
      return;
    }
    const currentVersion = await DeviceInfo.getVersion();
    if (!currentVersion) return;
    const isSupportedVersion = compareVersions(currentVersion, '2.334.0') >= 0;
    if (!isSupportedVersion) return;
    NativeModules.ReactNativeBridge.updateMemberMe(params);
  }

  getFeatureFlag(
    params: Parameters<ReactNativeBridge['getFeatureFlag']>[0],
  ): Promise<string | null> {
    if (isWeb) {
      return Promise.resolve(null);
    }
    try {
      return NativeModules.ReactNativeBridge.getFeatureFlag(params);
    } catch {
      return Promise.resolve(null);
    }
  }

  async openShareSheet(message: string) {
    if (isWeb) {
      try {
        await navigator.clipboard.writeText(message);
      } catch {
        /* clipboard API not available */
      }
      return;
    }
    NativeModules.ReactNativeBridge.openShareSheet(message);
  }

  openImageShareSheet(url: string) {
    if (isWeb) {
      return;
    }
    NativeModules.ReactNativeBridge.openImageShareSheet(url);
  }

  openInstagramStoryShare(url: string) {
    if (isWeb) {
      return;
    }
    NativeModules.ReactNativeBridge.openInstagramStoryShare(url);
  }

  getGoodsMeasureInfo(params: {
    goods_sno: number;
    full_length: number | null;
  }) {
    if (isWeb) {
      return Promise.resolve(null);
    }
    try {
      return NativeModules.ReactNativeBridge.getGoodsMeasureInfo(params);
    } catch {
      return Promise.resolve(null);
    }
  }

  openGoodsMeasureInfoInputBottomSheet(params: {
    goods_sno: number;
    full_length: number | null;
  }) {
    if (isWeb) {
      return Promise.resolve(null);
    }
    try {
      return NativeModules.ReactNativeBridge.openGoodsMeasureInfoInputBottomSheet(
        params,
      );
    } catch {
      return Promise.resolve(null);
    }
  }
}

export const bridge = new Bridge();
